import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid';
import {
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { api } from '../utils/apiService';
import { useTranslation } from '../hooks/useTranslation';
import { API_CONFIG } from '../utils/config';
import defaultLogo from '../assets/logoEazy.png';
import { arabicRegularBase64, arabicBoldBase64 } from '../assets/fonts/Arabicfontbase64';

// Helper to construct full URL for an image path from backend
const getImageUrl = (imagePath) => {
  if (!imagePath || typeof imagePath !== 'string') return '';
  if (imagePath.startsWith('data:') || imagePath.startsWith('blob:')) {
    return imagePath;
  }
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const cleanBase = (API_CONFIG?.BASE_URL || 'https://dev.foxiomlabs.com/eazyfatoora').replace(/\/+$/, '');
  const cleanPath = imagePath.replace(/^\/+/, '');
  return `${cleanBase}/${cleanPath}`;
};

// Helper to convert an image URL or path to Base64 data URI for jsPDF
const urlToBase64 = async (url) => {
  if (!url) return null;
  if (typeof url === 'string' && url.startsWith('data:image')) {
    return url;
  }

  const fullUrl = getImageUrl(url);
  if (!fullUrl) return null;

  // 1. Try fetch with blob
  try {
    const res = await fetch(fullUrl, { mode: 'cors' });
    if (res.ok) {
      const blob = await res.blob();
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
      if (base64 && typeof base64 === 'string' && base64.startsWith('data:image')) {
        return base64;
      }
    }
  } catch (err) {
    // Fall back to Image element
  }

  // 2. Try Image canvas approach
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      } catch (err) {
        resolve(null);
      }
    };
    img.onerror = () => {
      resolve(null);
    };
    img.src = fullUrl;
  });
};

// Format date in "DD MMM YYYY" format e.g. "25 Sept 2026"
const formatReportDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return String(date);
  const day = String(d.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
};

// Format date & time in Saudi Arabia time (Asia/Riyadh, UTC+3) e.g. "26 Sept 2026, 9:20:22 AM"
const formatReportDateTime = (date = new Date()) => {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Riyadh',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    const parts = formatter.formatToParts(d);
    let day = '', month = '', year = '', hour = '', minute = '', second = '', dayPeriod = '';
    for (const p of parts) {
      if (p.type === 'day') day = p.value;
      if (p.type === 'month') {
        month = p.value === 'Sep' ? 'Sept' : p.value;
      }
      if (p.type === 'year') year = p.value;
      if (p.type === 'hour') hour = p.value;
      if (p.type === 'minute') minute = p.value;
      if (p.type === 'second') second = p.value;
      if (p.type === 'dayPeriod') dayPeriod = p.value.toUpperCase();
    }
    return `${day} ${month} ${year}, ${hour}:${minute}:${second} ${dayPeriod}`;
  } catch (err) {
    // Fallback: convert to UTC+3 manually
    const utcTime = d.getTime() + d.getTimezoneOffset() * 60000;
    const saudiDate = new Date(utcTime + 3600000 * 3);
    const day = String(saudiDate.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[saudiDate.getMonth()];
    const year = saudiDate.getFullYear();

    let hours = saudiDate.getHours();
    const minutes = String(saudiDate.getMinutes()).padStart(2, '0');
    const seconds = String(saudiDate.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${day} ${month} ${year}, ${hours}:${minutes}:${seconds} ${ampm}`;
  }
};

// Check if a column represents numeric / monetary data
const isNumericCol = (col) => {
  if (col.type === 'number') return true;
  if (col.align === 'right' || col.headerAlign === 'right') return true;
  const fieldLower = (col.field || '').toLowerCase();
  const headerLower = (col.headerName || '').toLowerCase();
  return (
    fieldLower.includes('amount') ||
    fieldLower.includes('total') ||
    fieldLower.includes('vat') ||
    fieldLower.includes('sales') ||
    fieldLower.includes('price') ||
    fieldLower.includes('cost') ||
    fieldLower.includes('balance') ||
    fieldLower.includes('paid') ||
    fieldLower.includes('discount') ||
    fieldLower.includes('subtotal') ||
    fieldLower.includes('tax') ||
    headerLower.includes('amount') ||
    headerLower.includes('total') ||
    headerLower.includes('vat') ||
    headerLower.includes('sales') ||
    headerLower.includes('balance') ||
    headerLower.includes('paid') ||
    headerLower.includes('price') ||
    headerLower.includes('discount')
  );
};

// Format number with 2 decimal places and comma thousands separator (e.g. 2,698.73)
const formatNumericValue = (val) => {
  if (val === null || val === undefined || val === '') return '0.00';
  if (typeof val === 'number') {
    return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  const cleanStr = String(val).replace(/,/g, '').trim();
  const num = parseFloat(cleanStr.replace(/[^0-9.-]+/g, ''));
  if (isNaN(num)) return String(val);
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hasArabic = (str) => /[\u0600-\u06FF]/.test(String(str || ''));

const prepareArabicText = (str) => {
  if (!str) return '';
  const text = String(str);
  if (!hasArabic(text)) return text;

  const reshaped = text;
  
  const tokens = reshaped.split(/(\s+|[-|,.:;!?()]+)/);
  
  const classifiedTokens = tokens.map(token => ({
    text: token,
    isRTL: /[\u0600-\u06FF]/.test(token)
  }));
  
  const groups = [];
  let currentGroup = null;
  
  for (const token of classifiedTokens) {
    if (!currentGroup || currentGroup.isRTL !== token.isRTL) {
      currentGroup = { isRTL: token.isRTL, tokens: [token.text] };
      groups.push(currentGroup);
    } else {
      currentGroup.tokens.push(token.text);
    }
  }
  
  const processedGroups = groups.map(group => {
    if (group.isRTL) {
      return group.tokens.reverse().join('');
    } else {
      return group.tokens.join('');
    }
  });
  
  return processedGroups.reverse().join('');
};

const setTextFont = (doc, str, style = 'normal') => {
  if (hasArabic(str)) {
    doc.setFont('Amiri', style === 'bold' ? 'bold' : 'normal');
  } else {
    doc.setFont('helvetica', style);
  }
};

const fitText = (doc, text, maxWidth) => {
  if (!text) return '';
  let str = String(text);
  if (doc.getTextWidth(str) <= maxWidth) return str;
  while (str.length > 1 && doc.getTextWidth(str + '…') > maxWidth) {
    str = str.slice(0, -1);
  }
  return str.trim() + '…';
};

const drawBuildingVectorIcon = (doc, x, y) => {
  doc.setFillColor(0, 102, 204);

  doc.rect(x + 0.5, y + 4.5, 3.8, 9.5, 'F');

  doc.rect(x + 4.8, y + 0.5, 5.2, 13.5, 'F');

  doc.rect(x + 10.5, y + 3.5, 3.8, 10.5, 'F');

  doc.setFillColor(255, 255, 255);

  for (let r = 0; r < 3; r++) {
    doc.rect(x + 1.2, y + 5.8 + r * 2.4, 1.0, 1.4, 'F');
    doc.rect(x + 2.6, y + 5.8 + r * 2.4, 1.0, 1.4, 'F');
  }

  for (let r = 0; r < 5; r++) {
    doc.rect(x + 5.6, y + 2.0 + r * 2.2, 1.5, 1.3, 'F');
    doc.rect(x + 7.7, y + 2.0 + r * 2.2, 1.5, 1.3, 'F');
  }

  for (let r = 0; r < 4; r++) {
    doc.rect(x + 11.2, y + 4.8 + r * 2.2, 1.0, 1.3, 'F');
    doc.rect(x + 12.6, y + 4.8 + r * 2.2, 1.0, 1.3, 'F');
  }
};


const CustomToolbar = (props) => {
  const { t: hookT } = useTranslation();
  const {
    showSearch,
    searchText,
    setSearchText,
    onRefresh,
    handleExportExcel,
    handleExportPDF,
    handlePrint,
    showExport,
    showPrint,
    customToolbar,
    t = hookT,
  } = props;

  return (
    <GridToolbarContainer
      sx={{
        p: 2,
        borderBottom: '2px solid rgba(67, 97, 238, 0.1)',
        bgcolor: '#fafbfc',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: '100%' }}>
        {/* Left side - Grid tools */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <GridToolbarColumnsButton
            sx={{
              fontSize: '0.875rem',
              color: '#4361ee',
              '&:hover': { bgcolor: 'rgba(67, 97, 238, 0.08)' }
            }}
          />
          <GridToolbarFilterButton
            sx={{
              fontSize: '0.875rem',
              color: '#4361ee',
              '&:hover': { bgcolor: 'rgba(67, 97, 238, 0.08)' }
            }}
          />
          <GridToolbarDensitySelector
            sx={{
              fontSize: '0.875rem',
              color: '#4361ee',
              '&:hover': { bgcolor: 'rgba(67, 97, 238, 0.08)' }
            }}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Right side - Custom actions */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Search */}
          {showSearch && (
            <TextField
              size="small"
              placeholder={t('Reports.Search_Records')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: '1.1rem', color: '#4361ee' }} />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 280,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  height: '38px',
                  bgcolor: 'white',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4361ee',
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4361ee',
                      borderWidth: '2px',
                    }
                  }
                },
              }}
            />
          )}

          {/* Refresh */}
          {onRefresh && (
           <Tooltip title={t('Reports.Refresh_Data')} arrow>
              <IconButton
                size="small"
                onClick={onRefresh}
                sx={{
                  borderRadius: '6px',
                  bgcolor: 'white',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  '&:hover': {
                    bgcolor: 'rgba(67, 97, 238, 0.08)',
                    borderColor: '#4361ee',
                  }
                }}
              >
                <RefreshIcon sx={{ fontSize: '1.2rem', color: '#4361ee' }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Export buttons */}
          {showExport && (
            <>
              <Tooltip title={t('Reports.Export_To_Excel')} arrow>
                <IconButton
                  size="small"
                  onClick={handleExportExcel}
                  sx={{
                    borderRadius: '6px',
                    bgcolor: 'white',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    '&:hover': {
                      bgcolor: 'rgba(29, 111, 66, 0.08)',
                      borderColor: '#1D6F42',
                    }
                  }}
                >
                  <ExcelIcon sx={{ fontSize: '1.2rem', color: '#1D6F42' }} />
                </IconButton>
              </Tooltip>
             <Tooltip title={t('Reports.Export_To_PDF')} arrow>
                <IconButton
                  size="small"
                  onClick={handleExportPDF}
                  sx={{
                    borderRadius: '6px',
                    bgcolor: 'white',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    '&:hover': {
                      bgcolor: 'rgba(211, 47, 47, 0.08)',
                      borderColor: '#D32F2F',
                    }
                  }}
                >
                  <PdfIcon sx={{ fontSize: '1.2rem', color: '#D32F2F' }} />
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* Print */}
          {showPrint && (
            <Tooltip title={t('Reports.Print_Report')} arrow>
              <IconButton
                size="small"
                onClick={handlePrint}
                sx={{
                  borderRadius: '6px',
                  bgcolor: 'white',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    borderColor: 'rgba(0, 0, 0, 0.3)',
                  }
                }}
              >
                <PrintIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Custom toolbar items */}
          {customToolbar}
        </Box>
      </Box>
    </GridToolbarContainer>
  );
};

const ReportDataGrid = ({
  title,
  data = [],
  columns = [],
  loading = false,
  onRefresh,
  exportFileName = 'report',
  pageSize = 25,
  showSearch = true,
  showExport = true,
  showPrint = true,
  customToolbar,
  height = 600,
  density = 'standard', // 'compact', 'standard', 'comfortable'
  checkboxSelection = false,
  onRowClick,
  getRowId,
  sx = {},
  totals,
  dateRange,
  companyInfo,
}) => {
  const [searchText, setSearchText] = useState('');
  const { t } = useTranslation();
  const [companyDetails, setCompanyDetails] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: pageSize,
  });

  // Fetch company profile data for PDF and Print header
  useEffect(() => {
    let isMounted = true;
    const fetchCompanyProfile = async () => {
      try {
        const cachedTenant = localStorage.getItem('tenant');
        if (cachedTenant) {
          try {
            const parsed = JSON.parse(cachedTenant);
            if (isMounted) setCompanyDetails(parsed);
            const cachedLogo = parsed.logo || parsed.image || parsed.companyLogo;
            if (cachedLogo) {
              const fullUrl = getImageUrl(cachedLogo);
              urlToBase64(fullUrl).then((b64) => {
                if (isMounted && b64) setLogoBase64(b64);
              });
            }
          } catch (e) {}
        }
        const res = await api.get('/company/profile');
        if (res.data) {
          const details = res.data.companyDetails || res.data;
          if (isMounted) setCompanyDetails(details);
          const rawLogo = details.logo || details.companyLogo || details.image;
          if (rawLogo) {
            const fullUrl = getImageUrl(rawLogo);
            urlToBase64(fullUrl).then((b64) => {
              if (isMounted && b64) setLogoBase64(b64);
            });
          }
        }
      } catch (err) {
        // Fallback gracefully
      }
    };
    fetchCompanyProfile();
    return () => {
      isMounted = false;
    };
  }, []);

  // Filter data based on search
  const filteredData = React.useMemo(() => {
    if (!searchText) return data;

    return data.filter((row) => {
      return Object.values(row).some((value) => {
        if (value === null || value === undefined) return false;
        return value.toString().toLowerCase().includes(searchText.toLowerCase());
      });
    });
  }, [data, searchText]);

  // Helper: extract a plain-text value from a column for export
  const getCellValue = (col, row) => {
    const value = row[col.field];
    if (col.valueFormatter) {
      const formatted = col.valueFormatter({ value, row });
      if (formatted !== null && formatted !== undefined) return formatted;
    }
    if (col.renderCell) {
      const rendered = col.renderCell({ value, row, field: col.field });
      if (typeof rendered === 'string' || typeof rendered === 'number') {
        return rendered;
      }
    }
    if (typeof value === 'number' && !Number.isInteger(value)) {
      return parseFloat(value.toFixed(2));
    }
    return value !== null && value !== undefined ? value : '';
  };

  const getPDFCellValue = (col, row) => {
    const value = row[col.field];
    const isDate = (col.field || '').toLowerCase().includes('date');
    if (isDate && value) {
      return formatReportDate(value);
    }
    if (isNumericCol(col)) {
      return formatNumericValue(value);
    }
    if (col.valueFormatter) {
      const formatted = col.valueFormatter({ value, row });
      if (formatted !== null && formatted !== undefined) return String(formatted);
    }
    if (typeof value === 'number') {
      return formatNumericValue(value);
    }
    return value !== null && value !== undefined ? String(value) : '';
  };

  // Export to Excel
  const handleExportExcel = () => {
    const exportRows = filteredData.map((row, index) => {
      const obj = { '#': index + 1 };
      columns.forEach((col) => {
        obj[col.headerName || col.field] = getCellValue(col, row);
      });
      return obj;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

    // Auto-size columns
    const maxWidth = 50;
    const colWidths = [{ wch: 6 }, ...columns.map((col) => ({
      wch: Math.min(maxWidth, Math.max(col.headerName?.length || 10, 10)),
    }))];
    worksheet['!cols'] = colWidths;

    XLSX.writeFile(workbook, `${exportFileName}.xlsx`);
  };

  // Export to PDF matching the reference template exactly (A4 portrait size)
  const handleExportPDF = async () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth(); // 210mm
    const marginX = 14;
    const rightX = pageWidth - marginX; // 196mm

    // Register the Arabic-capable font once per document
    doc.addFileToVFS('Amiri-Regular.ttf', arabicRegularBase64);
    doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
    doc.addFileToVFS('Amiri-Bold.ttf', arabicBoldBase64);
    doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold');

    // 1. Resolve Company Details
    const compName =
      companyInfo?.name ||
      companyDetails?.companyName ||
      companyDetails?.name ||
      'Test Company';

    let compAddress = '123 Business Street, Dubai, UAE';
    if (companyInfo?.address) {
      compAddress = companyInfo.address;
    } else if (companyDetails?.address) {
      if (typeof companyDetails.address === 'string') {
        compAddress = companyDetails.address;
      } else if (typeof companyDetails.address === 'object') {
        const parts = [
          companyDetails.address.buildingNo ? `Bldg ${companyDetails.address.buildingNo}` : '',
          companyDetails.address.street || '',
          companyDetails.address.city || '',
          companyDetails.address.country || '',
        ].filter(Boolean);
        if (parts.length > 0) compAddress = parts.join(', ');
      }
    }

    const compPhone =
      companyInfo?.phone ||
      companyDetails?.contact?.phone ||
      companyDetails?.phone ||
      '+971 50 123 4567';

    const compEmail =
      companyInfo?.email ||
      companyDetails?.contact?.email ||
      companyDetails?.email ||
      'info@testcompany.com';

    const compVatNo =
      companyInfo?.vatNo ||
      companyInfo?.trn ||
      companyDetails?.vatNo ||
      companyDetails?.trn ||
      companyDetails?.registration?.taxNumber ||
      '345678960545763';

    const compCRN =
      companyInfo?.crn ||
      companyInfo?.companyRegNumber ||
      companyDetails?.companyRegNumber ||
      companyDetails?.registration?.regNo ||
      companyDetails?.crNo ||
      '';

    const vatCrnText = compCRN
      ? `VAT No: ${compVatNo}   |   CRN: ${compCRN}`
      : `VAT No: ${compVatNo}`;

    const telEmailText = `Tel: ${compPhone}   |   Email: ${compEmail}`;

    // 2. Draw Company Logo
    let logoDrawn = false;
    let resolvedLogo = logoBase64;
    const rawLogo = companyInfo?.logo || companyDetails?.logo;
    if (!resolvedLogo && rawLogo) {
      resolvedLogo = await urlToBase64(rawLogo);
    }
    if (!resolvedLogo && defaultLogo) {
      resolvedLogo = await urlToBase64(defaultLogo);
    }

    if (resolvedLogo && typeof resolvedLogo === 'string' && resolvedLogo.startsWith('data:image')) {
      try {
        const match = resolvedLogo.match(/^data:image\/([a-zA-Z0-9+]+);base64,/);
        const format = match ? match[1].toUpperCase() : 'PNG';
        const safeFormat = (format === 'JPEG' || format === 'JPG') ? 'JPEG' : 'PNG';
        doc.addImage(resolvedLogo, safeFormat, marginX, 11, 15.5, 15.5);
        logoDrawn = true;
      } catch (e) {
        logoDrawn = false;
      }
    }
    if (!logoDrawn) {
      drawBuildingVectorIcon(doc, marginX, 11.5);
    }
    const textStartX = marginX + 18.5;
    const leftBlockWidth = pageWidth * 0.52 - textStartX;
    doc.setFontSize(13); 
    setTextFont(doc, compName, 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(fitText(doc, compName, leftBlockWidth), textStartX, 15);

    doc.setFontSize(8.5);
    setTextFont(doc, compAddress, 'normal');
    doc.setTextColor(71, 85, 105); 
    doc.text(fitText(doc, compAddress, leftBlockWidth), textStartX, 19.5);

    setTextFont(doc, vatCrnText, 'normal');
    doc.text(fitText(doc, vatCrnText, leftBlockWidth), textStartX, 23.5);

    setTextFont(doc, telEmailText, 'normal');
    doc.text(fitText(doc, telEmailText, leftBlockWidth), textStartX, 27.5);
    let reportTitle = title;
    if (!reportTitle) {
      const cleanName = (exportFileName || 'Report')
        .replace(/-\d{4}-\d{2}-\d{2}.*$/, '')
        .replace(/[-_]/g, ' ')
        .trim();
      reportTitle = cleanName
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      if (!reportTitle.toLowerCase().includes('report')) {
        reportTitle += ' Report';
      }
    }

    let periodText = '';
    if (dateRange?.startDate && dateRange?.endDate) {
      periodText = `${formatReportDate(dateRange.startDate)} - ${formatReportDate(dateRange.endDate)}`;
    } else {
      const dateValues = [];
      filteredData.forEach((row) => {
        ['invoiceDate', 'date', 'createdAt', 'paymentDate', 'period'].forEach((field) => {
          if (row[field]) {
            const parsed = new Date(row[field]);
            if (!isNaN(parsed.getTime())) dateValues.push(parsed);
          }
        });
      });
      if (dateValues.length > 0) {
        dateValues.sort((a, b) => a - b);
        periodText = `${formatReportDate(dateValues[0])} - ${formatReportDate(dateValues[dateValues.length - 1])}`;
      } else {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        periodText = `${formatReportDate(startOfMonth)} - ${formatReportDate(today)}`;
      }
    }

    const generatedOnText = formatReportDateTime(new Date());

    const rightBlockWidth = pageWidth * 0.42; 

    doc.setFontSize(12); 
    setTextFont(doc, reportTitle, 'bold');
    doc.setTextColor(0, 98, 210); 
    doc.text(fitText(doc, reportTitle, rightBlockWidth), rightX, 15, { align: 'right' });

    doc.setFontSize(8.5);
    setTextFont(doc, periodText, 'normal');
    doc.setTextColor(71, 85, 105); 
    doc.text(fitText(doc, `Report Period: ${periodText}`, rightBlockWidth), rightX, 20, { align: 'right' });
    doc.text(fitText(doc, `Generated On: ${generatedOnText}`, rightBlockWidth), rightX, 25, { align: 'right' });

    doc.setDrawColor(226, 232, 240); 
    doc.setLineWidth(0.35);
    doc.line(marginX, 30.5, rightX, 30.5);

    const tableHeaders = ['#', ...columns.map((col) => prepareArabicText(col.headerName || col.field))];
    const tableRows = filteredData.map((row, idx) => [
      String(idx + 1),
      ...columns.map((col) => {
        const val = getPDFCellValue(col, row);
        return prepareArabicText(val);
      }),
    ]);

    const firstNumericIdx = columns.findIndex((col) => isNumericCol(col));
    const textColSpan = firstNumericIdx !== -1 ? firstNumericIdx + 1 : columns.length + 1;

    const footRow = [
      {
        content: 'Grand Total',
        colSpan: textColSpan,
        styles: {
          halign: 'left',
          fontStyle: 'bold',
          textColor: [15, 23, 42],
          fontSize: 9,
          cellPadding: { top: 3.5, bottom: 3.5, left: 3, right: 3 },
        },
      },
    ];

    if (firstNumericIdx !== -1) {
      for (let i = firstNumericIdx; i < columns.length; i++) {
        const col = columns[i];
        if (isNumericCol(col)) {
          let totalVal = null;
          const fieldLower = (col.field || '').toLowerCase();

          if (totals) {
            if (
              fieldLower.includes('subtotal') ||
              (fieldLower.includes('amount') &&
                !fieldLower.includes('vat') &&
                !fieldLower.includes('total'))
            ) {
              totalVal =
                totals.subTotal ??
                totals.totalAmount ??
                totals.totalSales ??
                totals.amount;
            } else if (fieldLower.includes('vat')) {
              totalVal = totals.totalVAT ?? totals.vatAmount ?? totals.vat;
            } else if (
              fieldLower.includes('total') ||
              fieldLower.includes('grandtotal')
            ) {
              totalVal =
                totals.grandTotal ??
                totals.totalAmount ??
                totals.totalSales;
            } else if (fieldLower.includes('paid')) {
              totalVal = totals.totalPaid ?? totals.paid;
            } else if (fieldLower.includes('balance')) {
              totalVal = totals.totalBalance ?? totals.balance;
            } else if (fieldLower.includes('discount')) {
              totalVal = totals.totalDiscount ?? totals.discount;
            } else if (
              fieldLower.includes('invoice') ||
              fieldLower.includes('count')
            ) {
              totalVal = totals.totalInvoices ?? totals.invoiceCount;
            } else if (totals[col.field] !== undefined) {
              totalVal = totals[col.field];
            }
          }

          if (totalVal === null || totalVal === undefined) {
            const sum = filteredData.reduce((acc, row) => {
              const rawVal = row[col.field];
              const num =
                typeof rawVal === 'number'
                  ? rawVal
                  : parseFloat(String(rawVal || '').replace(/[^0-9.-]+/g, ''));
              return acc + (isNaN(num) ? 0 : num);
            }, 0);
            totalVal = sum;
          }

          const formattedTotal =
            totalVal !== null && totalVal !== undefined
              ? (typeof totalVal === 'number'
                  ? totalVal
                  : parseFloat(String(totalVal).replace(/[^0-9.-]+/g, ''))
                ).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : '';

          footRow.push({
            content: formattedTotal,
            styles: {
              halign: 'right',
              fontStyle: 'bold',
              textColor: [15, 23, 42],
              fontSize: 9,
              cellPadding: { top: 3.5, bottom: 3.5, left: 2.5, right: 2.5 },
            },
          });
        } else {
          footRow.push({
            content: '',
            styles: { halign: 'left' },
          });
        }
      }
    }

    const columnStyles = {
      0: { halign: 'center', cellWidth: 10 }, // '#' column
    };

    columns.forEach((col, idx) => {
      const colNumber = idx + 1;
      if (isNumericCol(col)) {
        columnStyles[colNumber] = { halign: 'right' };
      } else if ((col.field || '').toLowerCase().includes('date')) {
        columnStyles[colNumber] = { halign: 'left' };
      } else {
        columnStyles[colNumber] = { halign: col.align || 'left' };
      }
    });
    autoTable(doc, {
      head: [tableHeaders],
      body: tableRows,
      foot: [footRow],
      showFoot: 'lastPage',
      startY: 33.5,
      margin: { left: marginX, right: marginX, top: 12, bottom: 12 },
      styles: {
        font: 'Amiri',
        fontSize: 8,
        textColor: [30, 41, 59], 
        cellPadding: { top: 2.8, bottom: 2.8, left: 2.5, right: 2.5 },
        lineColor: [235, 240, 248],
        lineWidth: 0.1,
        overflow: 'linebreak',
      },
      headStyles: {
        font: 'Amiri',
        fillColor: [0, 98, 210], 
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8.5,
        cellPadding: { top: 3.2, bottom: 3.2, left: 2.5, right: 2.5 },
        lineColor: [0, 98, 210],
      },
      alternateRowStyles: {
        fillColor: [248, 250, 253], 
      },
      footStyles: {
        font: 'Amiri',
        fillColor: [235, 243, 253],
        textColor: [15, 23, 42], 
        fontStyle: 'bold',
        fontSize: 9,
        cellPadding: { top: 3.5, bottom: 3.5, left: 2.5, right: 2.5 },
        lineColor: [235, 243, 253],
        lineWidth: 0,
      },
      columnStyles: columnStyles,
    });

    doc.save(`${exportFileName}.pdf`);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');

    const compName =
      companyInfo?.name ||
      companyDetails?.companyName ||
      companyDetails?.name ||
      'Test Company';

    let compAddress = '123 Business Street, Dubai, UAE';
    if (companyInfo?.address) {
      compAddress = companyInfo.address;
    } else if (companyDetails?.address) {
      if (typeof companyDetails.address === 'string') {
        compAddress = companyDetails.address;
      } else if (typeof companyDetails.address === 'object') {
        const parts = [
          companyDetails.address.buildingNo ? `Bldg ${companyDetails.address.buildingNo}` : '',
          companyDetails.address.street || '',
          companyDetails.address.city || '',
          companyDetails.address.country || '',
        ].filter(Boolean);
        if (parts.length > 0) compAddress = parts.join(', ');
      }
    }

    const compPhone =
      companyInfo?.phone ||
      companyDetails?.contact?.phone ||
      companyDetails?.phone ||
      '+971 50 123 4567';

    const compEmail =
      companyInfo?.email ||
      companyDetails?.contact?.email ||
      companyDetails?.email ||
      'info@testcompany.com';

    const compVatNo =
      companyInfo?.vatNo ||
      companyInfo?.trn ||
      companyDetails?.vatNo ||
      companyDetails?.trn ||
      companyDetails?.registration?.taxNumber ||
      '345678960545763';

    const compCRN =
      companyInfo?.crn ||
      companyInfo?.companyRegNumber ||
      companyDetails?.companyRegNumber ||
      companyDetails?.registration?.regNo ||
      companyDetails?.crNo ||
      '';

    const vatCrnText = compCRN
      ? `VAT No: ${compVatNo} &nbsp;|&nbsp; CRN: ${compCRN}`
      : `VAT No: ${compVatNo}`;

    const telEmailText = `Tel: ${compPhone} &nbsp;|&nbsp; Email: ${compEmail}`;

    const rawLogo = companyInfo?.logo || companyDetails?.logo;
    const displayLogoUrl = rawLogo ? getImageUrl(rawLogo) : (logoBase64 || defaultLogo || '');

    let reportTitle = title;
    if (!reportTitle) {
      const cleanName = (exportFileName || 'Report')
        .replace(/-\d{4}-\d{2}-\d{2}.*$/, '')
        .replace(/[-_]/g, ' ')
        .trim();
      reportTitle = cleanName
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      if (!reportTitle.toLowerCase().includes('report')) {
        reportTitle += ' Report';
      }
    }

    let periodText = '';
    if (dateRange?.startDate && dateRange?.endDate) {
      periodText = `${formatReportDate(dateRange.startDate)} - ${formatReportDate(dateRange.endDate)}`;
    } else {
      const dateValues = [];
      filteredData.forEach((row) => {
        ['invoiceDate', 'date', 'createdAt', 'paymentDate', 'period'].forEach((field) => {
          if (row[field]) {
            const parsed = new Date(row[field]);
            if (!isNaN(parsed.getTime())) dateValues.push(parsed);
          }
        });
      });
      if (dateValues.length > 0) {
        dateValues.sort((a, b) => a - b);
        periodText = `${formatReportDate(dateValues[0])} - ${formatReportDate(dateValues[dateValues.length - 1])}`;
      } else {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        periodText = `${formatReportDate(startOfMonth)} - ${formatReportDate(today)}`;
      }
    }

    const generatedOnText = formatReportDateTime(new Date());

    // Calculate footer totals
    const firstNumericIdx = columns.findIndex((col) => isNumericCol(col));
    const textColSpan = firstNumericIdx !== -1 ? firstNumericIdx + 1 : columns.length + 1;

    const numericTotals = [];
    if (firstNumericIdx !== -1) {
      for (let i = firstNumericIdx; i < columns.length; i++) {
        const col = columns[i];
        if (isNumericCol(col)) {
          let totalVal = null;
          const fieldLower = (col.field || '').toLowerCase();
          if (totals) {
            if (
              fieldLower.includes('subtotal') ||
              (fieldLower.includes('amount') &&
                !fieldLower.includes('vat') &&
                !fieldLower.includes('total'))
            ) {
              totalVal =
                totals.subTotal ??
                totals.totalAmount ??
                totals.totalSales ??
                totals.amount;
            } else if (fieldLower.includes('vat')) {
              totalVal = totals.totalVAT ?? totals.vatAmount ?? totals.vat;
            } else if (
              fieldLower.includes('total') ||
              fieldLower.includes('grandtotal')
            ) {
              totalVal =
                totals.grandTotal ??
                totals.totalAmount ??
                totals.totalSales;
            } else if (fieldLower.includes('paid')) {
              totalVal = totals.totalPaid ?? totals.paid;
            } else if (fieldLower.includes('balance')) {
              totalVal = totals.totalBalance ?? totals.balance;
            } else if (fieldLower.includes('discount')) {
              totalVal = totals.totalDiscount ?? totals.discount;
            } else if (
              fieldLower.includes('invoice') ||
              fieldLower.includes('count')
            ) {
              totalVal = totals.totalInvoices ?? totals.invoiceCount;
            } else if (totals[col.field] !== undefined) {
              totalVal = totals[col.field];
            }
          }

          if (totalVal === null || totalVal === undefined) {
            const sum = filteredData.reduce((acc, row) => {
              const rawVal = row[col.field];
              const num =
                typeof rawVal === 'number'
                  ? rawVal
                  : parseFloat(String(rawVal || '').replace(/[^0-9.-]+/g, ''));
              return acc + (isNaN(num) ? 0 : num);
            }, 0);
            totalVal = sum;
          }

          const formattedTotal =
            totalVal !== null && totalVal !== undefined
              ? (typeof totalVal === 'number'
                  ? totalVal
                  : parseFloat(String(totalVal).replace(/[^0-9.-]+/g, ''))
                ).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : '0.00';

          numericTotals.push({ field: col.field, total: formattedTotal });
        }
      }
    }

    const tableHTML = `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <title>${reportTitle}</title>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
          <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap" rel="stylesheet" />
          <style>
            @page {
              size: A4 portrait;
              margin: 10mm 12mm;
            }
            * { box-sizing: border-box; }
            body {
              font-family: 'Cairo', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, "Noto Sans Arabic", sans-serif;
              padding: 0;
              margin: 0;
              color: #1e293b;
              font-size: 11px;
            }
            .header-container {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding-bottom: 10px;
              border-bottom: 1.5px solid #e2e8f0;
              margin-bottom: 12px;
              gap: 16px;
            }
            .company-box {
              display: flex;
              align-items: flex-start;
              gap: 10px;
              flex: 1 1 58%;
              max-width: 58%;
              min-width: 0;
            }
            .company-logo {
              width: 46px;
              height: 46px;
              object-fit: contain;
              flex-shrink: 0;
              margin-top: 2px;
            }
            .company-info {
              min-width: 0;
              flex: 1;
              overflow: hidden;
            }
            .company-name {
              font-size: 11.5px;
              font-weight: 700;
              color: #0f172a;
              margin-bottom: 2px;
              unicode-bidi: plaintext;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .company-detail {
              font-size: 9.5px;
              color: #475569;
              line-height: 1.4;
              unicode-bidi: plaintext;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .report-box {
              text-align: right;
              flex: 0 1 40%;
              max-width: 40%;
              min-width: 0;
            }
            .report-title {
              font-size: 11.5px;
              font-weight: 700;
              color: #0062d2;
              margin-bottom: 3px;
              unicode-bidi: plaintext;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .report-meta {
              font-size: 9.5px;
              color: #475569;
              line-height: 1.4;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 4px;
            }
            th {
              background-color: #0062d2;
              color: #ffffff;
              font-weight: 700;
              font-size: 10px;
              padding: 6px 8px;
              text-align: left;
            }
            th.center { text-align: center; }
            th.right { text-align: right; }
            td {
              padding: 5px 8px;
              font-size: 9.5px;
              color: #1e293b;
              border-bottom: 1px solid #edf2f7;
              unicode-bidi: plaintext;
            }
            td.center { text-align: center; }
            td.right { text-align: right; }
            tr:nth-child(even) td {
              background-color: #f8fafc;
            }
            tfoot {
              display: table-row-group;
            }
            tfoot tr td {
              background-color: #ebf3fd;
              font-weight: 700;
              font-size: 10.5px;
              color: #0f172a;
              padding: 7px 8px;
              border-top: 1px solid #dce7f6;
              border-bottom: none;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="header-container">
            <div class="company-box">
              ${displayLogoUrl ? `<img src="${displayLogoUrl}" class="company-logo" alt="Logo" onerror="this.style.display='none'" />` : ''}
              <div class="company-info">
                <div class="company-name" title="${compName}"><bdi>${compName}</bdi></div>
                <div class="company-detail" title="${compAddress}"><bdi>${compAddress}</bdi></div>
                <div class="company-detail">${vatCrnText}</div>
                <div class="company-detail">${telEmailText}</div>
              </div>
            </div>
            <div class="report-box">
              <div class="report-title" title="${reportTitle}">${reportTitle}</div>
              <div class="report-meta">Report Period: ${periodText}</div>
              <div class="report-meta">Generated On: ${generatedOnText}</div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th class="center" style="width: 35px;">#</th>
                ${columns.map(col => {
                  const isNum = isNumericCol(col);
                  return `<th class="${isNum ? 'right' : 'left'}">${col.headerName || col.field}</th>`;
                }).join('')}
              </tr>
            </thead>
            <tbody>
              ${filteredData.map((row, idx) => `
                <tr>
                  <td class="center">${idx + 1}</td>
                  ${columns.map(col => {
                    const isNum = isNumericCol(col);
                    const val = getPDFCellValue(col, row);
                    return `<td class="${isNum ? 'right' : 'left'}">${val}</td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="${textColSpan}">Grand Total</td>
                ${numericTotals.map(t => `<td class="right">${t.total}</td>`).join('')}
              </tr>
            </tfoot>
          </table>
          <script>
            window.onload = () => {
              // Wait for the Cairo webfont to actually finish loading before
              // printing - otherwise the browser falls back to a font with
              // no Arabic glyphs and Arabic text renders as boxes/garbage.
              var doPrint = function () {
                window.print();
                window.onafterprint = () => window.close();
              };
              if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(doPrint).catch(doPrint);
              } else {
                setTimeout(doPrint, 300);
              }
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(tableHTML);
    printWindow.document.close();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 0,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Header */}
      {title && (
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6" fontWeight="600">
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {filteredData.length} record{filteredData.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* DataGrid */}
      <DataGrid
        rows={filteredData}
        columns={columns}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50, 100]}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick
        onRowClick={onRowClick}
        getRowId={getRowId}
        density={density}
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: {
            showSearch,
            searchText,
            setSearchText,
            onRefresh,
            handleExportExcel,
            handleExportPDF,
            handlePrint,
            showExport,
            showPrint,
            customToolbar,
            t
          }
        }}
        sx={{
          border: 'none',
          bgcolor: 'white',
          '& .MuiDataGrid-cell': {
            borderRight: '1px solid rgba(224, 224, 224, 0.8)',
            borderBottom: '1px solid rgba(224, 224, 224, 0.8)',
            fontSize: '0.875rem',
            color: '#2c3e50',
            py: 1.5,
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            },
            '&:last-child': {
              borderRight: 'none',
            },
          },
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: '#f8f9fa',
            borderBottom: '2px solid #4361ee',
            borderRadius: 0,
            minHeight: '48px !important',
            maxHeight: '48px !important',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: '1px solid rgba(224, 224, 224, 1)',
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            },
            '&:last-child': {
              borderRight: 'none',
            },
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            fontSize: '0.875rem',
            color: '#031b4e',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:nth-of-type(even)': {
              bgcolor: '#fafbfc',
            },
            '&:hover': {
              bgcolor: 'rgba(67, 97, 238, 0.06)',
              transform: 'scale(1.001)',
              boxShadow: '0 2px 4px rgba(67, 97, 238, 0.1)',
            },
            '&.Mui-selected': {
              bgcolor: 'rgba(67, 97, 238, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(67, 97, 238, 0.12)',
              },
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '2px solid rgba(0, 0, 0, 0.08)',
            bgcolor: '#f8f9fa',
            minHeight: '52px',
          },
          '& .MuiTablePagination-root': {
            color: '#2c3e50',
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
            fontWeight: 500,
          },
          '& .MuiDataGrid-virtualScroller': {
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                bgcolor: '#a8a8a8',
              },
            },
          },
          '& .MuiDataGrid-overlay': {
            bgcolor: 'rgba(255, 255, 255, 0.9)',
          },
          '& .MuiCircularProgress-root': {
            color: '#4361ee',
          },
          '& .font-weight-bold': {
            fontWeight: 700,
            color: '#031b4e',
          },
          '& .text-error': {
            color: '#d32f2f',
            fontWeight: 600,
          },
          height: height,
        }}
      />
    </Paper>
  );
};

export default ReportDataGrid;